/**
 * returnUrl.ts
 *
 * Helpers for saving and consuming a post-login redirect URL.
 * Used when an unauthenticated user opens a deep link (e.g. from a Teams card).
 *
 * Security: only same-origin relative paths are accepted — external URLs and
 * protocol-relative paths (//) are rejected to prevent open-redirect attacks.
 */

const STORAGE_KEY = 'insight_return_url';

/**
 * Returns true when `url` is a safe, same-origin relative path that can be
 * used as a post-login redirect destination.
 *
 * Accepts:  /rec/applicant/search/42/profile?tab=messages
 * Rejects:  https://evil.com, //evil.com, javascript:…, empty string, /
 */
export function isSafeReturnUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  // Must start with a single slash, not // (protocol-relative)
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return false;
  // Reject protocol-relative or absolute URLs that sneak through
  try {
    // If it parses as an absolute URL it has a host — reject it
    const parsed = new URL(trimmed, window.location.origin);
    if (parsed.origin !== window.location.origin) return false;
  } catch {
    return false;
  }
  // Don't bother saving the root or login page itself
  if (trimmed === '/' || trimmed === '') return false;
  return true;
}

/**
 * Persists `url` to sessionStorage for post-login redirect.
 * Silently skips unsafe or trivial URLs (/, /dashboard).
 */
export function saveReturnUrl(url: string): void {
  if (!isSafeReturnUrl(url)) return;
  // Don't save the login / dashboard pages — they're not meaningful return targets
  const path = url.split('?')[0];
  if (path === '/dashboard' || path === '/') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, url);
  } catch {
    // sessionStorage unavailable (private browsing edge case) — silent fail
  }
}

/**
 * Returns the stored return URL and removes it from sessionStorage (consume-once).
 * Returns `null` when nothing is stored or the stored value is no longer safe.
 */
export function consumeReturnUrl(): string | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (stored && isSafeReturnUrl(stored)) return stored;
  } catch {
    // sessionStorage unavailable — fall through
  }
  return null;
}
